import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar.jsx';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';

const COLORS = {
  purple: ['#EEEDFE', '#3C3489'],
  teal: ['#E1F5EE', '#085041'],
  coral: ['#FAECE7', '#712B13'],
  blue: ['#E6F1FB', '#0C447C'],
  amber: ['#FAEEDA', '#633806'],
  pink: ['#FBEAF0', '#72243E']
};

export default function Dashboard() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/apps')
      .then((r) => setApps(r.data.apps))
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-medium">Your apps</h1>
            <p className="text-sm text-muted mt-1">
              {loading ? 'Loading…' : `${apps.length} app${apps.length === 1 ? '' : 's'} available`}
            </p>
          </div>
          {user?.role === 'ADMIN' && (
            <Link
              to="/apps/new"
              className="text-sm bg-ink text-white px-4 py-2 rounded-lg hover:opacity-90"
            >
              + Add app
            </Link>
          )}
        </div>

        {error && <div className="text-sm text-red-600 mb-4">{error}</div>}

        {!loading && apps.length === 0 && (
          <div className="border border-dashed border-border rounded-xl p-10 text-center text-muted">
            {user?.role === 'ADMIN' ? (
              <>No apps yet. Click <span className="font-medium">Add app</span> to register your first one.</>
            ) : (
              <>No apps available yet. Check back soon.</>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {apps.map((a) => {
            const [bg, fg] = COLORS[a.iconColor] || COLORS.purple;
            return (
              <div
                key={a.id}
                className="bg-white border border-border rounded-xl p-4 hover:border-neutral-300 transition group relative"
              >
              <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                {user?.role === 'ADMIN' && (
                  <button
                    onClick={() => navigate(`/apps/${a.id}/access`)}
                    className="text-xs text-muted hover:text-ink"
                    title="Manage access"
                  >
                    Access
                  </button>
                )}
                {user?.role === 'ADMIN' && (
                  <button
                    onClick={() => navigate(`/apps/${a.id}/edit`)}
                    className="text-xs text-muted hover:text-ink"
                    title="Edit app"
                  >
                    Edit
                  </button>
                )}
              </div>
                <button
                  onClick={() => navigate(`/apps/${a.id}`)}
                  className="text-left w-full"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center font-medium text-sm mb-3"
                    style={{ background: bg, color: fg }}
                  >
                    {a.iconLabel}
                  </div>
                  <div className="text-sm font-medium mb-1">{a.name}</div>
                  <div className="text-xs text-muted line-clamp-2 mb-3" style={{ minHeight: 32 }}>{a.description}</div>
                  <div className="flex flex-wrap gap-1">
                    <span className="inline-block text-[11px] bg-neutral-100 text-muted px-2 py-0.5 rounded-full">
                      {a.category}
                    </span>
                    <span className="inline-block text-[11px] bg-neutral-100 text-muted px-2 py-0.5 rounded-full">
                      {authLabel(a.authType)}
                    </span>
                    {a.anthropicApiKeyId && (
                      <span className="inline-block text-[11px] bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">
                        Anthropic synced
                      </span>
                    )}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </main>
    </>
  );
}

function authLabel(t) {
  return { SSO: 'SSO', SEPARATE: 'Separate login', APIKEY: 'API key', NONE: 'Open' }[t] || t;
}
